/**
 * NEON CLIENT
 * ===========
 *
 * Single client for Neon Auth (Managed Better Auth) and the Neon Data API.
 * Uses SupabaseAuthAdapter for API compatibility with the previous
 * @supabase/supabase-js integration (signInWithPassword, onAuthStateChange…)
 *
 * IMPORTANT: Use the config-object form of createClient, NOT the string-URL
 * form. The config form accepts the adapter builder function directly, whereas
 * the string-URL overloads expect the adapter instance — a type inconsistency
 * that causes overload resolution failures with the builder.
 */

import { createClient, SupabaseAuthAdapter } from '@neondatabase/neon-js';
import type { Database } from './types';

// Public HTTPS endpoints (not secrets — derived from the Neon branch URLs).
// Vite inlines these at build time, so a value missing from the build
// environment becomes `undefined` in the bundle and every auth and Data API
// request silently goes to "undefined/...". Fail loudly instead: these must be
// set wherever the app is built (locally in .env, and in the Vercel project's
// environment variables for deploys).
function requireEnv(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  if (!value) {
    throw new Error(
      `${name} is not set. Add the VITE_NEON_* variables to .env for local ` +
        `builds and to the Vercel project settings for deployed builds.`,
    );
  }
  return value;
}

const NEON_AUTH_URL = requireEnv('VITE_NEON_AUTH_URL');
const NEON_DATA_API_URL = requireEnv('VITE_NEON_DATA_API_URL');

// Base URL of the deployed `api` Neon Function.
export const NEON_FUNCTIONS_URL = requireEnv('VITE_NEON_FUNCTIONS_URL');

// Config-object form — `auth.adapter` accepts the builder here.
export const client = createClient<Database>({
  auth: {
    url: NEON_AUTH_URL,
    adapter: SupabaseAuthAdapter(),
  },
  dataApi: { url: NEON_DATA_API_URL },
});

// ---- Type helpers -------------------------------------------------------
// Derive Session and User from the adapter's return type so we never have
// to import from a nested package path.

type _AuthClient = typeof client.auth;
type _GetSessionReturn = Awaited<ReturnType<_AuthClient['getSession']>>;
type _SessionData = _GetSessionReturn['data'];
// data is { session: Session | null } or the entire data is null in some paths
type _RawSession = _SessionData extends { session: infer S } ? S : never;
export type Session = NonNullable<_RawSession>;
export type User = Session['user'];

// ---- invokeFunction -------------------------------------------------------

interface InvokeOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  /** Plain object (JSON-encoded) or FormData */
  body?: unknown;
}

/**
 * Call the `api` Neon Function with the current user's JWT.
 * Drop-in replacement for `supabase.functions.invoke()`.
 */
export async function invokeFunction<T = unknown>(
  path: string,
  options: InvokeOptions = {},
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const sessionResult = await client.auth.getSession();
    const sessionData = sessionResult.data as { session?: Session | null } | null;
    const session = sessionData?.session ?? null;

    const headers: Record<string, string> = {};
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    let fetchBody: BodyInit | null = null;
    if (options.body instanceof FormData) {
      fetchBody = options.body;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      fetchBody = JSON.stringify(options.body);
    }

    const response = await fetch(`${NEON_FUNCTIONS_URL}${path}`, {
      method: options.method ?? 'POST',
      headers,
      body: fetchBody,
    });

    const payload = await response.json().catch(() => null) as unknown;

    if (!response.ok) {
      const message =
        payload !== null &&
        typeof payload === 'object' &&
        'error' in payload
          ? String((payload as { error: unknown }).error)
          : `Function ${path} failed with status ${response.status}`;
      return { data: null, error: new Error(message) };
    }

    return { data: payload as T, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error('Function invocation failed'),
    };
  }
}
